import React, { useState, useContext, useEffect } from 'react';
import { FaFire, FaLightbulb } from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';
import { RecommendationService } from '../services/RecommendationService';

// Use the same categories as in CategorySelect
const CATEGORIES = [
  'Politics',
  'Business',
  'Technology',
  'Science',
  'Health',
  'Arts',
  'Sports',
  'Opinion',
  'World',
  'Lifestyle'
];

export default function DiscoverPanel({ articles, onArticleClick }) {
  const { user } = useContext(AuthContext);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [personalizedArticles, setPersonalizedArticles] = useState([]);
  const [showPersonalized, setShowPersonalized] = useState(false);
  
  useEffect(() => {
    // Fetch personalized recommendations when the component mounts
    const fetchPersonalizedRecommendations = async () => {
      if (user) {
        try {
          const recommendations = await RecommendationService.getDiscoverRecommendations(user.username);
          if (recommendations && recommendations.length > 0) {
            setPersonalizedArticles(recommendations);
          }
        } catch (error) {
          console.error('Error fetching personalized recommendations:', error);
        }
      }
    };
    
    fetchPersonalizedRecommendations();
  }, [user]);
  
  if (!articles || articles.length === 0) return null;
  
  // Determine which articles to display
  const displayArticles = showPersonalized && personalizedArticles.length > 0 
    ? personalizedArticles 
    : articles;
  
  // Filter articles by selected category
  const filteredArticles = selectedCategory 
    ? displayArticles.filter(article => 
        article.categories && 
        article.categories.includes(selectedCategory)
      )
    : displayArticles;

  // Track article clicks for recommendations
  const handleArticleClick = (slug) => {
    const article = articles.find(a => a.slug === slug);
    
    if (article && user) {
      // Track the interaction
      RecommendationService.trackInteraction(
        user.username, 
        article.id || article.articleId, 
        'discover_click'
      );
    }
    
    onArticleClick(slug);
  };

  return (
    <div className="fixed top-16 right-0 w-72 h-screen bg-white shadow-md overflow-y-auto z-10">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <FaFire className="text-red-500 mr-2" />
            <h2 className="text-lg font-serif font-bold">Discover</h2>
          </div>
          
          {personalizedArticles.length > 0 && (
            <button 
              onClick={() => setShowPersonalized(!showPersonalized)}
              className={`flex items-center text-xs px-2 py-1 rounded-full ${
                showPersonalized ? 'bg-taupe text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <FaLightbulb className="mr-1" />
              For You
            </button>
          )}
        </div>
        
        {/* Category filters */}
        <div className="mb-4">
          <h3 className="text-sm font-medium mb-2">Filter by Category</h3>
          <div className="flex flex-wrap gap-1 mb-2">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-2 py-1 text-xs rounded-full ${
                selectedCategory === null
                  ? 'bg-taupe text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {CATEGORIES.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-2 py-1 text-xs rounded-full ${
                  selectedCategory === category
                    ? 'bg-taupe text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
        
        {/* Articles list */}
        <div className="space-y-4">
          {filteredArticles.length > 0 ? (
            filteredArticles.map((article) => (
              <div 
                key={article.slug || article.articleId}
                className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleArticleClick(article.slug || article.articleId)}
              >
                {article.imageUrl && (
                  <div className="h-32 bg-gray-200 overflow-hidden">
                    <img 
                      src={article.imageUrl} 
                      alt={article.title} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="p-3">
                  <h3 className="font-serif font-bold text-sm line-clamp-2">{article.title}</h3>
                  <p className="text-xs text-gray-500 mt-1">By {article.author}</p>
                  
                  {/* Display categories */}
                  {article.categories && article.categories.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {article.categories.slice(0, 2).map(category => (
                        <span 
                          key={category} 
                          className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full"
                        >
                          {category}
                        </span>
                      ))}
                      {article.categories.length > 2 && (
                        <span className="text-xs text-gray-500">+{article.categories.length - 2} more</span>
                      )}
                    </div>
                  )}
                  
                  {/* Show recommendation badge if personalized */}
                  {showPersonalized && (
                    <div className="mt-2">
                      <span className="px-2 py-0.5 bg-taupe text-white text-xs rounded-full">
                        Recommended for you
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-500 py-4">
              No articles found in this category
            </div>
          )}
        </div>
      </div>
    </div>
  );
}